import { ActionIcon, Box, Group, Stack, Text, Title } from "@mantine/core";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandTwitter,
  IconBrandYoutube,
} from "@tabler/icons-react";
import { SocialMedia, TikTokData } from "../../../../gql/__generated__/graphql";

interface SocialMediaStatsProps {
  socialMedia?: SocialMedia | null;
  tiktokData?: TikTokData | null;
  variant?: "compact" | "detailed" | "inline"; // compact for profiles, detailed for cards, inline for creator cards
}

// Format follower counts (e.g., 1500 -> 1.5K)
function formatFollowers(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

const SocialMediaStats = ({
  socialMedia,
  tiktokData,
  variant = "compact",
}: SocialMediaStatsProps) => {
  const isDetailed = variant === "detailed";
  const isInline = variant === "inline";

  const renderStat = (
    platform: string,
    icon: React.ReactNode,
    color: string,
    count: number,
    label: string,
    iconVariant: "light" | "filled" = "light"
  ) => {
    // Inline variant: just icon with number below, very compact
    if (isInline) {
      return (
        <Stack key={platform} gap={2} align="center">
          <ActionIcon variant={iconVariant} color={color} size="md" radius="xl">
            {icon}
          </ActionIcon>
          <Text size="sm" fw={600}>
            {formatFollowers(count)}
          </Text>
        </Stack>
      );
    }

    // Compact/detailed variants: original layout
    return (
      <Group key={platform} gap="xs">
        <ActionIcon variant={iconVariant} color={color} size="lg" radius="xl">
          {icon}
        </ActionIcon>
        <Stack gap={0}>
          <Text size="xs" tt="uppercase" fw={500}>
            {platform}
          </Text>
          <Text size="sm" fw={700}>
            {formatFollowers(count)}
          </Text>
        </Stack>
      </Group>
    );
  };

  const iconSize = isInline ? 18 : isDetailed ? 18 : 20;

  return (
    <Box>
      {isDetailed && (
        <Title order={4} fw={600} pb={"xs"}>
          Social Media Stats:
        </Title>
      )}
      <Group wrap={isInline ? "nowrap" : "wrap"} gap={"md"}>
        {tiktokData?.userInfo?.follower_count &&
          renderStat(
            "tiktok",
            <IconBrandTiktok size={iconSize} color="#FFFFFF" />,
            "#000000",
            tiktokData.userInfo.follower_count,
            "followers",
            "filled"
          )}
        {socialMedia?.instagram &&
          renderStat(
            "instagram",
            <IconBrandInstagram size={iconSize} color="#E1306C" />,
            "#C13584",
            socialMedia.instagram,
            "followers"
          )}
        {socialMedia?.youtube &&
          renderStat(
            "youtube",
            <IconBrandYoutube size={iconSize} color="#FF0000" />,
            "#FF0000",
            socialMedia.youtube,
            "subscribers"
          )}
        {socialMedia?.twitter &&
          renderStat(
            "twitter",
            <IconBrandTwitter size={iconSize} color="#1DA1F2" />,
            "#1DA1F2",
            socialMedia.twitter,
            "followers"
          )}
      </Group>
    </Box>
  );
};

export default SocialMediaStats;
